import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Sparkles, Calendar, LogOut } from 'lucide-react';

const LOGO = '/favicon-512.png';

/**
 * Premium waiting room — shown to:
 *  - clients who picked the "à l'unité" plan (single) → branded "septembre 2026"
 *  - clients without any chosen formula → invitation to activate their space
 */
export default function AwaitingAccess({ reason }) {
  const { user, logout } = useAuth();
  const isSingle = reason === 'single';
  const firstName = (user?.name || '').split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col">
      {/* Top minimal bar */}
      <header className="px-5 sm:px-8 py-5 flex justify-between items-center">
        <Link to="/" className="font-heading text-[20px] tracking-[3px] text-[#493329]">
          PURO
        </Link>
        <button
          onClick={logout}
          className="inline-flex items-center text-[12px] text-[#AE9885] hover:text-[#C99A55] transition-colors gap-1.5"
          data-testid="awaiting-logout"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
          Se déconnecter
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 sm:px-8 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-xl w-full text-center"
        >
          {/* Logo */}
          <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-7 opacity-90" />

          {/* Decorative icon */}
          <div className="w-14 h-14 mx-auto mb-7 rounded-full bg-[#C99A55]/10 flex items-center justify-center">
            {isSingle ? (
              <Calendar className="w-6 h-6 text-[#C99A55]" strokeWidth={1.4} />
            ) : (
              <Sparkles className="w-6 h-6 text-[#C99A55]" strokeWidth={1.4} />
            )}
          </div>

          {/* Eyebrow */}
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#C99A55] font-semibold mb-4">
            {isSingle ? 'Pré-lancement' : 'Activation requise'}
          </p>

          {/* Title — adaptive */}
          <h1 className="font-heading font-light text-3xl sm:text-4xl text-[#493329] tracking-tight leading-[1.15]" data-testid="awaiting-title">
            {isSingle ? (
              <>
                {firstName && <>Bonjour <em className="text-[#9A744C] not-italic">{firstName}</em>,<br /></>}
                votre place est sécurisée.
              </>
            ) : (
              <>
                {firstName && <>Bonjour <em className="text-[#9A744C] not-italic">{firstName}</em>,<br /></>}
                une dernière étape avant l'accès.
              </>
            )}
          </h1>

          <div className="w-10 h-0.5 bg-[#C99A55] mx-auto my-7" />

          {/* Body — adaptive */}
          {isSingle ? (
            <>
              <p className="text-[16px] sm:text-[17px] text-[#4A453E] leading-[1.7] font-light max-w-md mx-auto" data-testid="awaiting-message">
                Votre accès au planning sera débloqué prochainement lors de l'ouverture officielle des réservations.
              </p>

              {/* Premium opening date pill */}
              <div className="mt-9 inline-flex flex-col items-center gap-1 px-7 py-5 rounded-2xl bg-white border border-[#E9DDCC] shadow-[0_8px_30px_rgba(28,26,23,0.04)]">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#AE9885] font-medium">Ouverture du studio</span>
                <span className="font-heading text-3xl sm:text-4xl text-[#493329] font-light tracking-tight">Septembre 2026</span>
                <span className="text-[11px] text-[#9A744C] italic mt-1">Vous serez prévenue par email.</span>
              </div>

              <p className="text-xs text-[#AE9885] mt-9 leading-relaxed max-w-sm mx-auto italic">
                En attendant, profitez de votre temps : nous préparons un accueil à la hauteur de la confiance que vous nous accordez.
              </p>

              <div className="mt-10 flex flex-col items-center gap-3">
                <Link to="/abonnement">
                  <Button
                    variant="outline"
                    className="border-[#C99A55] text-[#9A744C] hover:bg-[#C99A55] hover:text-white rounded-full px-8 h-11 transition-all duration-400 text-[13px]"
                    data-testid="awaiting-upgrade-btn"
                  >
                    Passer à une formule mensuelle
                  </Button>
                </Link>
                <Link
                  to="/cours-prive"
                  className="text-[12px] text-[#AE9885] underline underline-offset-4 decoration-[#C99A55]/40 hover:text-[#C99A55] transition-colors"
                >
                  Demander une expérience privée
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-[16px] sm:text-[17px] text-[#4A453E] leading-[1.7] font-light max-w-md mx-auto" data-testid="awaiting-message">
                Votre accès sera activé dès qu'une formule aura été sélectionnée.
              </p>

              <p className="text-sm text-[#7A6455] mt-5 max-w-md mx-auto leading-relaxed">
                Choisissez la formule qui vous correspond pour finaliser votre pré-inscription
                et bénéficier de votre <span className="text-[#9A744C] font-medium">offre de lancement −20%</span>.
              </p>

              <div className="mt-9">
                <Link to="/abonnement">
                  <Button
                    className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full px-9 h-12 transition-all duration-400 text-[14px] shadow-[0_6px_24px_rgba(197,164,78,0.25)]"
                    data-testid="awaiting-choose-plan-btn"
                  >
                    Choisir ma formule
                  </Button>
                </Link>
              </div>

              <p className="text-xs text-[#AE9885] mt-8 italic max-w-sm mx-auto leading-relaxed">
                Aucun paiement ne sera prélevé avant l'ouverture du studio.
              </p>
            </>
          )}
        </motion.div>
      </main>

      <footer className="text-center py-6 px-5 border-t border-[#E9DDCC]">
        <p className="text-[11px] text-[#AE9885] tracking-wide">PURO Studio Reformer · Pilates Reformer premium</p>
      </footer>
    </div>
  );
}
